/**
 * Pausa del plan (retención al darse de baja, 18 oct 2026).
 *
 * Al aceptar «Pausa tu plan 1 o 2 meses» la suscripción de Stripe sigue viva
 * (`active`) con `pause_collection: { behavior: 'void', resumes_at }`: no se
 * cobra nada hasta `resumes_at`. En el tenant:
 *
 *   - `pausedFrom`: fin del periodo ya pagado. Hasta ahí sigue con todo.
 *   - `pausedUntil`: el `resumes_at` de Stripe, cuando vuelve a cobrarse.
 *
 * Entre las dos fechas la cuenta está «sin plan» (ve todo, no suma), con su
 * propio texto. Vive aparte, sin importar nada, porque la usan a la vez
 * plans.ts (MRR) y finPrueba.ts (`sinPlanActivo`).
 */

export interface TenantConPausa {
  subscriptionStatus?: string;
  pausedFrom?: string;
  pausedUntil?: string;
}

/** ¿Tiene una pausa programada o en curso (que aún no terminó)? */
export function pausaPendiente(tenant: TenantConPausa | null | undefined, ahora: number = Date.now()): boolean {
  if (!tenant?.pausedUntil || tenant.subscriptionStatus === 'canceled') return false;
  const hasta = Date.parse(tenant.pausedUntil);
  return Number.isFinite(hasta) && hasta > ahora;
}

/** ¿Está ahora mismo en pausa? Ya pasó el periodo pagado y aún no vuelve a cobrarse. */
export function planEnPausa(tenant: TenantConPausa | null | undefined, ahora: number = Date.now()): boolean {
  if (!pausaPendiente(tenant, ahora)) return false;
  const desde = tenant?.pausedFrom ? Date.parse(tenant.pausedFrom) : Number.NaN;
  return !Number.isFinite(desde) || desde <= ahora;
}

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** «16 dic». */
function diaCorto(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${d} ${MESES_CORTOS[m - 1] ?? ''}`;
}

/** Lo que dice la API (y la web) cuando una cuenta en pausa intenta guardar algo. */
export function mensajePlanEnPausa(pausedUntil: string | undefined): string {
  const hasta = pausedUntil ? ` hasta el ${diaCorto(pausedUntil)}` : '';
  return `Tu plan está en pausa${hasta}. Tus datos siguen aquí y puedes verlos y descargarlos; para seguir sumando, reanuda tu plan desde Configuración › Tu plan.`;
}

/** Prefijo común del mensaje: la web lo reconoce para enseñar su aviso. */
export const PREFIJO_PLAN_EN_PAUSA = 'Tu plan está en pausa';
